import React, { useState, useEffect } from 'react';
import { Heart, Users, Church, DollarSign, Music, Award, UserPlus, Calendar, FileText, MessageSquare, Milestone, Smartphone, Search, Plus } from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection
import { useAuth } from '../contexts/AuthContext';
import { SacramentApprovalsModal } from './SacramentApprovalsModal';
import { BookingApprovalsModal } from './BookingApprovalsModal';
import { AdminDocUploadCard } from './AdminDocUploadCard';
import { ServicePlanner } from './ServicePlanner';
import { ChurchBranding } from './ChurchBranding';

interface StatItem {
  label: string;
  value: string;
  change: string;
  changeType: 'increase' | 'decrease' | 'neutral';
  icon: LucideIcon;
  color: string;
}

interface MpesaTransaction {
  mpesa_code: string;
  amount: number;
  sender_phone: string;
  type?: string;
  sender_name?: string; // Add this line (the '?' makes it optional)
  transaction_date?: string;
}

export const ChurchDashboard = ({ onViewChange }: { onViewChange: (view: string) => void }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [qrCode, setQrCode] = useState<string | null>(null);

  const [selectedStartDate, setSelectedStartDate] = useState<string>('');
  const [selectedEndDate, setSelectedEndDate] = useState<string>('');

  const [isSacramentsOpen, setIsSacramentsOpen] = useState(false);

  const handleExtractData = async () => {
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-church-bio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          shop_id: user?.shop_id, 
          start_date: selectedStartDate, 
          end_date: selectedEndDate
        }),
      });
    
      if (!response.ok) throw new Error('Export failed');
    
      // FIX: Process the response as a Blob (File) instead of JSON
      const blob = await response.blob();
      // Explicitly define the PDF type here
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlob);
    
      // Create a temporary link to trigger the download
      const a = document.createElement('a');
      a.href = url;
      a.download = `Church_Report_68.pdf`;
      document.body.appendChild(a);
      a.click();
    
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error("PDF Error:", error);
      alert("Error generating PDF report.");
    }
  };

  const handleLinkWhatsApp = async () => {
    try {
      // Replace with your actual Evolution API endpoint
      const response = await fetch(`https://whatsapp.tenear.com/${user?.shop_id}`);
      const data = await response.json();
      setQrCode(data.base64); // Logic to display QR in a modal or new tab
      if(data.base64) window.open(data.base64, '_blank');
    } catch (error) {
      alert("Could not fetch WhatsApp QR Code.");
    }
  };

  useEffect(() => {
    if (user?.department) {
      const dept = user.department.toLowerCase();
      
      // If the user belongs to a specific department that isn't 'admin', 
      // redirect them to their respective route defined in App.tsx
      switch (dept) {
        case 'media':
          navigate('/media-dashboard');
          break;
        case 'finance':
          navigate('/finance-dashboard');
          break;
        case 'development':
          navigate('/development-dashboard');
          break;
        // Add more cases as you onboard more departments
        case 'admin':
        default:
          // Stay on the main ChurchDashboard
          break;
      }
    }
  }, [user, navigate]);

  // --- STATE FOR WELFARE TRACKING ---
  const [memberSearch, setMemberSearch] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const stats: StatItem[] = [
    { label: 'Total Members', value: '410', change: '+12%', changeType: 'increase', icon: Users, color: 'text-blue-600' },
    { label: 'Avg. Attendance', value: '289', change: '+5%', changeType: 'increase', icon: Church, color: 'text-green-600' },
    { label: 'Monthly Tithes', value: 'KES 75k', change: '-2%', changeType: 'decrease', icon: Heart, color: 'text-red-600' },
    { label: 'New Visitors', value: '11', change: '+18%', changeType: 'increase', icon: UserPlus, color: 'text-purple-600' },
    { label: 'Active Volunteers', value: '18', change: '0%', changeType: 'neutral', icon: Milestone, color: 'text-orange-600' }
  ];

  const handleWelfareSubmit = async () => {
    if (!memberSearch || !amount) {
      alert("Please enter a member name/ID and an amount.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/post-to-church-welfare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_identifier: memberSearch,
          amount: parseFloat(amount),
          shop_id: user?.shop_id,
          recorded_by: (user as any)?.username || user?.id || 'Admin' 
        }),
      });

      if (!response.ok) throw new Error(`Server error: ${response.status}`);

      alert("Welfare contribution recorded successfully!");
      setMemberSearch('');
      setAmount('');
    } catch (error: any) {
      console.error("Error saving welfare:", error);
      alert("Failed to save contribution.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- STATE FOR MASTER MEMBERS DIRECTORY ---
  const [currentSubView, setCurrentSubView] = useState<'dashboard' | 'member_directory'>('dashboard');
  const [churchMembers, setChurchMembers] = useState<any[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [editingDirectoryMember, setEditingDirectoryMember] = useState<any | null>(null);

  // --- NEW STATE FOR FACILITY APPROVALS MODAL ---
  const [isApprovalsOpen, setIsApprovalsOpen] = useState(false);

  // Fetches master data from the members_welfare database table
  const fetchChurchMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-church-members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: user?.shop_id }),
      });
      const data = await response.json();
      setChurchMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed fetching general church members:", err);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  // Submits the modified inputs back to n8n
  const handleUpdateMemberDirectoryInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDirectoryMember) return;

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-church-members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingDirectoryMember,
          shop_id: user?.shop_id
        }),
      });

      if (response.ok) {
        alert("Member records synchronized successfully!");
        setEditingDirectoryMember(null);
        fetchChurchMembers(); // Auto-refresh rows
      } else {
        throw new Error("Update transmission rejected");
      }
    } catch (err) {
      alert("Failed to sync structural changes to database.");
    }
  };

  // --- NEW STATE FOR TITHES & OFFERINGS ---
  const [financeCategory, setFinanceCategory] = useState('Offertory');
  const [paymentType, setPaymentType] = useState('Cash');
  const [cashTotal, setCashTotal] = useState('');
  const [selectedMpesaTx, setSelectedMpesaTx] = useState<MpesaTransaction | null>(null);
  const [mpesaTransactions, setMpesaTransactions] = useState<MpesaTransaction[]>([]);

  // ADD THESE NEW STATES HERE:
  const [pdqReference, setPdqReference] = useState('');
  const [pdqAmount, setPdqAmount] = useState('');
  const [cheques, setCheques] = useState<Array<{ chequeNumber: string; bankName: string; amount: string }>>([
    { chequeNumber: '', bankName: '', amount: '' }
  ]);

  const [subCategory, setSubCategory] = useState<string>('');

  const [serviceName, setServiceName] = useState('English Service');

  useEffect(() => {
    const loadMpesaData = async () => {
      // Only fetch if MPESA is selected and we have a shop_id
      if (user?.shop_id && financeCategory) {

        try {
          const response = await fetch('https://n8n.tenear.com/webhook/fetch-church-mpesa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              shop_id: user.shop_id,
              action: 'FETCH_PENDING_MPESA',
              category: financeCategory
            }),
          });
        
          if (!response.ok) throw new Error('Network response was not ok');

          const data = await response.json();
          const normalizedData = Array.isArray(data) ? [...data] : [data];

          setMpesaTransactions(normalizedData); 
        } catch (error) {
          console.error("Failed to fetch MPESA transactions:", error);
        }
      }
    };

    loadMpesaData();
  }, [user, financeCategory]); // This runs whenever these two values change

  const handleFinanceSubmit = async () => {
    // 1. Precise validations before firing the network request
    if (paymentType === 'Cash' && !cashTotal) {
      alert("Please enter the total cash amount.");
      return;
    }
    if (paymentType === 'PDQ' && (!pdqReference || !pdqAmount)) {
      alert("Please enter both the terminal reference and the swiped amount.");
      return;
    }
    if (paymentType === 'Cheque') {
      const hasEmptyCheque = cheques.some(c => !c.chequeNumber || !c.bankName || !c.amount);
      if (hasEmptyCheque) {
        alert("Please completely fill out all added cheque rows.");
        return;
      }
    }

    setIsSubmitting(true);

    // 2. Build explicit field parameters based on the manual channel selection
    let calculatedAmount: number = 0;
    let extraFields: any = {};

    if (paymentType === 'Cash') {
      calculatedAmount = parseFloat(cashTotal) || 0;
    } else if (paymentType === 'PDQ') {
      calculatedAmount = parseFloat(pdqAmount) || 0;
      extraFields = {
        pdq_reference: pdqReference
      };
    } else if (paymentType === 'Cheque') {
      // Calculate the sum of all cheques to keep the main amount field relevant
      calculatedAmount = cheques.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      extraFields = {
        cheques_list: cheques.map(c => ({
          cheque_number: c.chequeNumber,
          bank_name: c.bankName,
          amount: parseFloat(c.amount) || 0
        }))
      };
    }

    try {
      // 1. Combine Main Category and Sub-Category safely if "Other" is selected
      const finalizedCategory = financeCategory === 'Other' 
        ? `Other: ${subCategory || 'General'}` 
        : financeCategory;

      await fetch('https://n8n.tenear.com/webhook/post-tithe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: finalizedCategory, // <--- Now carries "Other: Hall Booking", etc.
          payment_method: paymentType,
          amount: calculatedAmount,
          shop_id: user?.shop_id,
          recorded_by: (user as any)?.username || 'Admin',
          service_name: serviceName,
          timestamp: new Date().toISOString(),
          ...extraFields 
        }),
      });

      alert("Record saved successfully!");
  
      // 2. Reset your new subCategory state alongside your other input cleanups
      setCashTotal('');
      setPdqReference('');
      setPdqAmount('');
      setSubCategory(''); // Clears the sub-category dropdown menu upon success
      setCheques([{ chequeNumber: '', bankName: '', amount: '' }]);

    } catch (error) {
      alert("Error saving financial record.");
    } finally {
      setIsSubmitting(false);
    }
  }
  // --- UI RENDER ---
  <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-black text-gray-900 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-green-600" /> Contributions
      </h3>
      
      {/* NEW BUTTON GROUP */}
      <div className="flex gap-2">
        <button 
          onClick={handleExtractData}
          className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-bold hover:bg-blue-100 transition-colors"
        >
          <FileText className="w-3.5 h-3.5" /> Extract Data
        </button>
      
        <button 
          onClick={handleLinkWhatsApp}
          className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-[10px] font-bold hover:bg-green-100 transition-colors"
        >
          <MessageSquare className="w-3.5 h-3.5" /> Link WhatsApp
        </button>
      </div>
    </div>

    {/* Category Selection */} 
    <div> 
      <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Select Allocation</label> 
      {/* Changed grid-cols-3 to grid-cols-4 to cleanly fit the 4th button */}
      <div className="grid grid-cols-4 gap-1.5"> 
        {['Offertory', 'Tithes', 'Givings', 'Other'].map(cat => ( 
          <button 
            key={cat} 
            type="button" 
            onClick={() => {
              setFinanceCategory(cat);
              if (cat !== 'Other') setSubCategory(''); // Clears sub-category if switching back to Tithes/Offertory
            }} 
            className={`py-2 text-[10px] font-black rounded-xl border transition-all ${ 
              financeCategory === cat 
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm' 
                : 'bg-gray-50 text-gray-500 border-gray-100 hover:bg-gray-100' 
            }`} 
          > 
            {cat} 
          </button> 
        ))} 
      </div> 

      {/* NEW: Conditional Sub-Category Dropdown Rendering */}
      {financeCategory === 'Other' && (
        <div className="mt-3 space-y-1 animate-fadeIn">
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block">
            Specify Sub-Category
          </label>
          <div className="relative">
            <select 
              value={subCategory} 
              onChange={(e) => setSubCategory(e.target.value)} 
              className="w-full p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-blue-600 transition-all appearance-none cursor-pointer"
            >
              <option value="">-- Select Purpose --</option>
              <option value="Hall Booking">Hall Booking</option>
              <option value="General Donation">General Donation</option>
              <option value="Fund Raising">Fund Raising</option>
              <option value="Welfare / Benevolence">Welfare / Benevolence</option>
            </select>
            {/* Visual anchor chevron arrow decoration */} 
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400"> 
              <svg className="fill-current h-4 w-4"
                xmlns="http://w3.org"
                viewBox="0 0 20 20"
              > 
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/> 
              </svg>  
            </div>
          </div>
        </div>
      )}
 

      {/* Payment Type Toggle */}
      <div className="flex p-1 bg-gray-100 rounded-xl">
        {['Cash', 'Cheque', 'PDQ'].map(type => (
          <button
            key={type}
            onClick={() => setPaymentType(type)}
            className={`flex-1 py-2 text-xs font-black rounded-lg transition-all ${
              paymentType === type ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Dynamic Input based on Payment Type */}
      {paymentType === 'Cash' ? (
        <div className="animate-in fade-in slide-in-from-top-1">
          <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Total from Cashier</label>
          <input 
            type="number" 
            placeholder="Enter KES total..."
            className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-green-500"
            value={cashTotal}
            onChange={(e) => setCashTotal(e.target.value)}
          />
        </div>
      ) : paymentType === 'Cheque' ? (
        <div className="space-y-2 max-h-52 overflow-y-auto pr-1 animate-in fade-in slide-in-from-top-1">
          <div className="flex justify-between items-center mb-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Cheque Log Records</label>
            <button
              type="button"
              onClick={() => setCheques([...cheques, { chequeNumber: '', bankName: '', amount: '' }])}
              className="text-[10px] font-black text-blue-600 hover:underline"
            >
              + Add Row
            </button>
          </div>
          {cheques.map((item, idx) => (
            <div key={idx} className="p-2.5 bg-gray-50 rounded-xl border border-gray-100 space-y-1.5 relative">
              {cheques.length > 1 && (
                <button
                  type="button"
                  onClick={() => setCheques(cheques.filter((_, i) => i !== idx))}
                  className="absolute top-2 right-2 text-[10px] text-red-500 font-bold"
                >
                  Remove
                </button>
              )}
              <input 
                type="text" 
                placeholder="Cheque Number"
                className="w-full p-2 bg-white border border-gray-100 rounded-lg text-xs"
                value={item.chequeNumber}
                onChange={(e) => {
                  const updated = [...cheques];
                  updated[idx].chequeNumber = e.target.value;
                  setCheques(updated);
                }}
              />
              <div className="grid grid-cols-2 gap-1.5">
                <input 
                  type="text" 
                  placeholder="Bank Name"
                  className="w-full p-2 bg-white border border-gray-100 rounded-lg text-xs"
                  value={item.bankName}
                  onChange={(e) => {
                    const updated = [...cheques];
                    updated[idx].bankName = e.target.value;
                    setCheques(updated);
                  }}
                />
                <input 
                  type="number" 
                  placeholder="Amount KES"
                  className="w-full p-2 bg-white border border-gray-100 rounded-lg text-xs"
                  value={item.amount}
                  onChange={(e) => {
                    const updated = [...cheques];
                    updated[idx].amount = e.target.value;
                    setCheques(updated);
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : paymentType === 'PDQ' ? (
        <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Terminal Trace Reference</label>
            <input 
              type="text" 
              placeholder="Receipt / Reference No..."
              className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-green-500"
              value={pdqReference}
              onChange={(e) => setPdqReference(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Card Amount Counted</label>
            <input 
              type="number" 
              placeholder="Enter KES amount..."
              className="w-full p-3 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-green-500"
              value={pdqAmount}
              onChange={(e) => setPdqAmount(e.target.value)}
            />
          </div>
        </div>
      ) : null}

      <button 
        onClick={handleFinanceSubmit}
        disabled={isSubmitting}
        className="w-full bg-gray-900 text-white font-black py-3 rounded-xl hover:bg-black transition-all text-sm mt-2"
      >
        {isSubmitting ? 'Recording...' : 'Save Contribution'}
      </button>
    </div>
  </div>

  // --- STATE FOR NEWSLETTER ---
  const [newsletterContent, setNewsletterContent] = useState('');
  const [isSendingNewsletter, setIsSendingNewsletter] = useState(false);

  const handleNewsletterSubmit = async () => {
    if (!newsletterContent.trim()) {
      alert("Please enter a message for the newsletter.");
      return;
    }

    setIsSendingNewsletter(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/send-newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: newsletterContent,
          shop_id: user?.shop_id,
          church_name: "St. Barnabas Anglican Church",
          category: "NEWSLETTER",
          sent_by: (user as any)?.username || 'Admin'
        }),
      });

      if (!response.ok) throw new Error(`Gateway Error: ${response.status}`);

      alert("WhatsApp Newsletter queued for delivery!");
      setNewsletterContent(''); // Clear the input
    } catch (error) {
      console.error("Newsletter Error:", error);
      alert("Failed to send WhatsApp broadcast.");
    } finally {
      setIsSendingNewsletter(false);
    }
  };

  // Intercept layout painting to render the Member Directory Spreadsheet view instead
  if (currentSubView === 'member_directory') {
    const filteredDirectoryRows = churchMembers.filter(m => 
      `${m.first_name} ${m.last_name} ${m.membership_number}`.toLowerCase().includes(memberSearchQuery.toLowerCase())
    );

    return (
      <div className="bg-orange-50 min-h-screen p-6 max-w-7xl mx-auto space-y-6 animate-in fade-in duration-200">
        {/* Header Navigation Section */}
        <div className="flex justify-between items-center bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm">
          <div>
            <button 
              onClick={() => setCurrentSubView('dashboard')}
              className="text-xs font-bold text-blue-600 uppercase tracking-wider hover:underline"
            >
              ← Back to Administration Panel
            </button>
            <h2 className="text-xl font-black text-gray-900 mt-1">Master Members Directory</h2>
          </div>
          <input 
            type="text"
            placeholder="Search members by name or registration ID..."
            value={memberSearchQuery}
            onChange={(e) => setMemberSearchQuery(e.target.value)}
            className="p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none text-sm font-medium w-80 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Master Registry Table Grid */}
        <div className="bg-white border border-gray-100 rounded-[2rem] shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-blue-50/50 text-[11px] font-black uppercase text-blue-600 tracking-wider border-b border-gray-100">
                  <th className="p-4">Memb. No</th>
                  <th className="p-4">Full Name</th>
                  <th className="p-4">Department / Ministry</th>
                  <th className="p-4">Role Title Designation</th>
                  <th className="p-4">Account Status</th>
                  <th className="p-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 text-sm font-medium text-gray-700">
                {isLoadingMembers ? (
                  <tr><td colSpan={6} className="p-10 text-center text-gray-400 animate-pulse font-bold">Synchronizing Master Registry Matrix...</td></tr>
                ) : filteredDirectoryRows.length === 0 ? (
                  <tr><td colSpan={6} className="p-10 text-center text-gray-400 italic">No corresponding members matching search criteria found.</td></tr>
                ) : (
                  filteredDirectoryRows.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="p-4 font-bold text-blue-600">{member.membership_number || 'Pending'}</td>
                      <td className="p-4 font-bold text-gray-900">{member.first_name} {member.last_name}</td>
                      <td className="p-4">{member.department || 'General Congregation'}</td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full ${member.is_ministry_lead ? 'bg-purple-100 text-purple-700 border border-purple-200' : 'bg-gray-100 text-gray-600'}`}>
                          {member.role || 'Member'} {member.is_ministry_lead && '(Lead)'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 text-[10px] font-black rounded-full ${member.status ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {member.status ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button 
                          onClick={() => setEditingDirectoryMember({ ...member })}
                          className="text-xs bg-gray-900 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg transition-all shadow-sm font-bold"
                        >
                          Edit Profile
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Editing Overlay Modal Form Drawer */}
        {editingDirectoryMember && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <form onSubmit={handleUpdateMemberDirectoryInfo} className="bg-white rounded-[2.5rem] w-full max-w-md p-6 shadow-2xl space-y-4 animate-in zoom-in-95 duration-150">
              <h3 className="text-lg font-black text-gray-900 border-b pb-3 border-gray-100">
                Update Profile: {editingDirectoryMember.first_name} {editingDirectoryMember.last_name}
              </h3>

              <div>
                <label className="text-[10px] font-black text-gray-500 uppercase block mb-1">Membership Number</label>
                <input 
                  type="text"
                  value={editingDirectoryMember.membership_number || ''}
                  onChange={(e) => setEditingDirectoryMember({ ...editingDirectoryMember, membership_number: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-medium text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-gray-500 uppercase block mb-1">Department</label>
                <input 
                  type="text"
                  value={editingDirectoryMember.department || ''}
                  onChange={(e) => setEditingDirectoryMember({ ...editingDirectoryMember, department: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-medium text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-gray-500 uppercase block mb-1">Role Designation Title</label>
                <input 
                  type="text"
                  value={editingDirectoryMember.role || ''}
                  onChange={(e) => setEditingDirectoryMember({ ...editingDirectoryMember, role: e.target.value })}
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-medium text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-3 py-1">
                <input 
                  type="checkbox"
                  id="is_lead"
                  checked={editingDirectoryMember.is_ministry_lead || false}
                  onChange={(e) => setEditingDirectoryMember({ ...editingDirectoryMember, is_ministry_lead: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_lead" className="text-sm font-bold text-gray-700 cursor-pointer select-none">
                  Designate as Department Ministry Leader
                </label>
              </div>

              <div>
                <label className="text-[10px] font-black text-gray-500 uppercase block mb-1">Membership Account Status</label>
                <select
                  value={editingDirectoryMember.status ? 'true' : 'false'}
                  onChange={(e) => setEditingDirectoryMember({ ...editingDirectoryMember, status: e.target.value === 'true' })}
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none font-medium text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="true">Active Member (Full System Access)</option>
                  <option value="false">Inactive Status (Restricted Access)</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                  type="button"
                  onClick={() => setEditingDirectoryMember(null)}
                  className="flex-1 border border-gray-100 hover:bg-gray-50 text-gray-500 font-bold py-3.5 rounded-xl transition-colors text-sm text-center"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl transition-colors text-sm text-center shadow-md shadow-blue-100"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-orange-50 min-h-screen p-4 max-w-7xl mx-auto space-y-4">
      {/* HEADER: Title, Subtitle, and Logo */}
      <div className="flex justify-between items-center mb-2"> {/* Items center & reduced margin */}
        <div>
          <ChurchBranding departmentName="Admin Desk" />
        </div>
      </div>

      {/* Stats Row - Scaled Down */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
             <div className={`p-1.5 w-fit rounded-lg bg-opacity-10 mb-2 ${stat.color.replace('text', 'bg')}`}>
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
             </div>
             <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{stat.label}</p>
             <h4 className="text-lg font-black text-gray-900 mt-0.5">{stat.value}</h4>
             <span className={`text-[9px] font-bold mt-1 block ${stat.changeType === 'increase' ? 'text-green-500' : 'text-red-500'}`}>
                {stat.change} <span className="font-medium text-gray-400">vs last month</span>
             </span>
          </div>
        ))}
      </div>

      {/* 🛑 MANAGEMENT & OPERATIONS GRID BOARD 🛑 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-2 items-start">
  
        {/* CARD 1: Church Membership Registry */}
        <div 
          onClick={() => { setCurrentSubView('member_directory'); fetchChurchMembers(); }} 
          className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[1.5rem] p-4 text-white cursor-pointer shadow-xl transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between min-h-[110px] group"
        >
          <div className="space-y-0.5 text-left">
            <h3 className="text-xs font-black uppercase tracking-wider">Membership Registry</h3>
            <p className="text-[11px] opacity-80 font-medium leading-tight line-clamp-2">
              Manage master member files, roles, status flags, and update directories.
            </p>
          </div>
          <div className="flex justify-end mt-1">
            <div className="bg-white/20 p-1.5 rounded-lg group-hover:bg-white/30 transition-all">
              <Users className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        {/* CARD 2: Sacrament Desk */}
        <div 
          onClick={() => setIsSacramentsOpen(true)} 
          className="bg-gradient-to-br from-purple-700 to-indigo-900 rounded-[1.5rem] p-4 text-white cursor-pointer shadow-xl transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between min-h-[110px] group"
        >
          <div className="space-y-0.5 text-left">
            <h3 className="text-xs font-black uppercase tracking-wider">Sacrament Desk</h3>
            <p className="text-[11px] opacity-75 font-medium leading-tight line-clamp-2">
              Endorse incoming Baptism and Confirmation requests.
            </p>
          </div>
          <div className="flex justify-end mt-1">
            <div className="bg-white/10 p-1.5 rounded-lg group-hover:bg-white/20 transition-all">
              <Award className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        {/* CARD 3: Knowledge RAG Upload Engine Core */}
        <div className="w-full max-h-[110px] overflow-hidden rounded-[1.5rem]">
          {/* Mount your upload card component directly here */}
          <AdminDocUploadCard shopId={user?.shop_id || 68} userId={user?.id || 0} />
        </div>

        {/* CARD 4: Facility Requests */}
        <div 
          onClick={() => setIsApprovalsOpen(true)} 
          className="bg-gradient-to-br from-slate-700 to-slate-800 rounded-[1.5rem] p-4 text-white cursor-pointer shadow-xl transition-all duration-300 hover:scale-[1.01] flex flex-col justify-between min-h-[110px] group"
        >
          <div className="space-y-0.5 text-left">
            <h3 className="text-xs font-black uppercase tracking-wider">Facility Requests</h3>
            <p className="text-[11px] opacity-75 font-medium leading-tight line-clamp-2">
              Review, approve, or reject active hall & boardroom schedule logs.
            </p>
          </div>
          <div className="flex justify-end mt-1">
            <div className="bg-white/10 p-1.5 rounded-lg group-hover:bg-white/20 transition-all">
              <Calendar className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

      </div>
         


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
        {/* COLUMN 1 & 2 COMBINED: Unified Contributions Panel */}
        <div className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-sm border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* LEFT SIDE: MANUAL COUNTER DESK (CASH & CHEQUES ONLY) */}
          <div className="space-y-4 border-r border-gray-100 pr-0 md:pr-6 text-left">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-black text-gray-900 flex items-center gap-2 text-sm">
                <DollarSign className="w-4 h-4 text-green-600" /> Manual Counter Postings
              </h3>
            </div>

             {/* NEW: Church Service Dropdown Menu */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Select Service</label>
              <div className="relative">
                <select
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  className="w-full p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-blue-600 transition-all appearance-none cursor-pointer"
                >
                  <option value="Morning Glory">Morning Glory</option>
                  <option value="Youth Service">Youth Service</option>
                  <option value="Teens Service">Teens Service</option>
                  <option value="Children's Ministry">Children's Ministry</option>
                  <option value="English Service">English Service</option>
                  <option value="Kiswahili Service">Kiswahili Service</option>
                  <option value="Friday Service">Friday Service</option>
                  <option value="Kufuga Sanctuary">Kufuga Sanctuary</option>
                </select>
                {/* Visual anchor chevron arrow decoration */}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                  <svg className="fill-current h-4 w-4" xmlns="http://w3.org" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                  </svg>
                </div>
              </div>
            </div>

            {/* Category Selection */} 
            <div> 
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Select Allocation</label> 
              {/* Changed grid-cols-3 to grid-cols-4 to cleanly fit the 4th button */}
              <div className="grid grid-cols-4 gap-1.5"> 
                {['Offertory', 'Tithes', 'Givings', 'Other'].map(cat => ( 
                  <button 
                    key={cat} 
                    type="button" 
                    onClick={() => {
                      setFinanceCategory(cat);
                      if (cat !== 'Other') setSubCategory(''); // Clears sub-category if switching back to Tithes/Offertory
                    }} 
                    className={`py-2 text-[10px] font-black rounded-xl border transition-all ${ 
                      financeCategory === cat 
                        ? 'bg-blue-600 text-white border-blue-600 shadow-sm' 
                        : 'bg-gray-50 text-gray-500 border-gray-100 hover:bg-gray-100' 
                    }`} 
                  > 
                    {cat} 
                  </button> 
                ))} 
              </div> 

              {/* NEW: Conditional Sub-Category Dropdown Rendering */}
              {financeCategory === 'Other' && (
                <div className="mt-3 space-y-1 animate-fadeIn">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block">
                    Specify Sub-Category
                  </label>
                  <div className="relative">
                    <select 
                      value={subCategory} 
                      onChange={(e) => setSubCategory(e.target.value)} 
                      className="w-full p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-bold text-gray-700 outline-none focus:ring-2 focus:ring-blue-600 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">-- Select Purpose --</option>
                      <option value="Hall Booking">Hall Booking</option>
                      <option value="General Donation">General Donation</option>
                      <option value="Fund Raising">Fund Raising</option>
                      <option value="Welfare / Benevolence">Welfare / Benevolence</option>
                    </select>
                    {/* Visual anchor chevron arrow decoration */} 
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400"> 
                      <svg className="fill-current h-4 w-4" 
                        xmlns="http://w3.org" 
                        viewBox="0 0 20 20"
                      > 
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/> 
                      </svg> 
                    </div>
                  </div>
                </div>
              )}
            </div>


            {/* Manual Payment Mode Toggle */}
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-wider block mb-1">Payment Method</label>
              <div className="flex p-1 bg-gray-100 rounded-xl">
                {['Cash', 'Cheque', 'PDQ'].map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setPaymentType(type)}
                    className={`flex-1 py-1.5 text-xs font-black rounded-lg transition-all ${
                      paymentType === type ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Conditional Input Fields */}
            {paymentType === 'Cash' ? (
              <div className="space-y-1 animate-in fade-in duration-150">
                <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Total Cash Counted (KES)</label>
                <input 
                  type="number" 
                  placeholder="Enter total hard currency sum..."
                  className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600 transition-all"
                  value={cashTotal}
                  onChange={(e) => setCashTotal(e.target.value)}
                />
              </div>
            ) : paymentType === 'Cheque' ? (
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1 animate-in fade-in duration-150 text-left">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Cheque Log Records</label>
                  <button
                    type="button"
                    onClick={() => setCheques([...cheques, { chequeNumber: '', bankName: '', amount: '' }])}
                    className="text-[10px] font-black text-blue-600 hover:underline"
                  >
                    + Add Row
                  </button>
                </div>
                {cheques.map((item, idx) => (
                  <div key={idx} className="p-2.5 bg-gray-50 rounded-xl border border-gray-100 space-y-1.5 relative">
                    {cheques.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setCheques(cheques.filter((_, i) => i !== idx))}
                        className="absolute top-2 right-2 text-[10px] text-red-500 font-bold"
                      >
                        Remove
                      </button>
                    )}
                    <input 
                      type="text" 
                      placeholder="Cheque Number"
                      className="w-full p-2 bg-white border border-gray-100 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500"
                      value={item.chequeNumber}
                      onChange={(e) => {
                        const updated = [...cheques];
                        updated[idx].chequeNumber = e.target.value;
                        setCheques(updated);
                      }}
                    />
                    <div className="grid grid-cols-2 gap-1.5">
                      <input 
                        type="text" 
                        placeholder="Bank Name"
                        className="w-full p-2 bg-white border border-gray-100 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500"
                        value={item.bankName}
                        onChange={(e) => {
                          const updated = [...cheques];
                          updated[idx].bankName = e.target.value;
                          setCheques(updated);
                        }}
                      />
                      <input 
                        type="number" 
                        placeholder="Amount KES"
                        className="w-full p-2 bg-white border border-gray-100 rounded-lg text-xs outline-none focus:ring-1 focus:ring-blue-500"
                        value={item.amount}
                        onChange={(e) => {
                          const updated = [...cheques];
                          updated[idx].amount = e.target.value;
                          setCheques(updated);
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : paymentType === 'PDQ' ? (
              <div className="space-y-2 animate-in fade-in duration-150 text-left">
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Terminal Trace Reference</label>
                  <input 
                    type="text" 
                    placeholder="Receipt / Reference No..."
                    className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600 transition-all"
                    value={pdqReference}
                    onChange={(e) => setPdqReference(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">Card Amount Counted</label>
                  <input 
                    type="number" 
                    placeholder="Enter KES amount..."
                    className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600 transition-all"
                    value={pdqAmount}
                    onChange={(e) => setPdqAmount(e.target.value)}
                  />
                </div>
              </div>
            ) : null}


            {/* WIRING UP BUTTON EXPLICITLY TO ACTION FOR EXTENSION HANDLERS */}
            <button 
              type="button" 
              onClick={handleFinanceSubmit}
              disabled={isSubmitting}
              className="w-full py-3 bg-gray-900 text-white text-xs font-black rounded-xl hover:bg-black transition-all uppercase tracking-wider shadow-sm disabled:bg-gray-400 mt-4"
            >
              {isSubmitting ? 'Posting Records...' : 'Post Counter Records'}
            </button>
          </div>

          {/* RIGHT SIDE: READ-ONLY LIVE MPESA FEED AUDIT CARD */}
          <div className="space-y-3 text-left flex flex-col justify-between h-full">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-black text-gray-900 flex items-center gap-1.5 text-sm">
                  <Smartphone className="w-4 h-4 text-blue-600" /> Live M-PESA Stream
                </h3>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black bg-blue-50 text-blue-700 uppercase tracking-widest animate-pulse">
                  ● Connected Live
                </span>
              </div>
              <p className="text-[11px] text-gray-400 font-medium leading-normal mb-3">
                Automated view-only transaction feed incoming directly via church shortcode webhooks.
              </p>

              {/* Live Scrolling Activity List */}
              <div className="space-y-2 max-h-[190px] overflow-y-auto pr-1">
                {mpesaTransactions.length === 0 ? (
                  <div className="p-8 bg-gray-50 rounded-2xl border border-dashed border-gray-200 text-center text-xs text-gray-400 font-medium italic">
                    Awaiting network collection records...
                  </div>
                ) : (
                  mpesaTransactions.map((tx, index) => (
                    <div key={index} className="p-3 bg-gray-50 border border-gray-100 rounded-xl flex justify-between items-center text-xs shadow-2xs hover:bg-gray-100/70 transition-colors">
                      <div className="space-y-0.5">
                        <p className="font-black text-gray-800 uppercase">{tx.mpesa_code || 'TX_REF'}</p>
                        <p className="text-[10px] text-gray-400 font-bold">{tx.sender_phone || 'Anonymous'}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-blue-600">KES {tx.amount}</p>
                        <span className="text-[9px] font-black uppercase text-gray-400 px-1.5 py-0.5 bg-white border border-gray-200 rounded-md">
                          {tx.type || 'Giving'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Utility Download Handles */}
            <div className="flex gap-2 mt-4 md:mt-0">
              <button 
                onClick={handleExtractData}
                className="flex-1 flex items-center justify-center gap-1 py-2 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-blue-100 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" /> PDF Statement
              </button>
              <button 
                onClick={handleLinkWhatsApp}
                className="flex-1 flex items-center justify-center gap-1 py-2 bg-green-50 text-green-700 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-green-100 transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5" /> Link Notify
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content: Service Planner */}
        <div className="lg:col-span-2"> 
          <ServicePlanner shopId={user?.shop_id?.toString() || ""} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Welfare Management Card */}
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-black text-gray-900 flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500 fill-red-500" /> Welfare Tracking
              </h3>
              <span className="text-[10px] bg-red-50 text-red-600 px-2 py-1 rounded-lg font-black uppercase">
                Treasurer
              </span>
            </div>
            
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-3 text-gray-400" size={16} />
                <input 
                  type="text" 
                  placeholder="Member Name or ID..." 
                  className="w-full text-sm p-3 pl-11 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
              </div>
              
              <div className="flex gap-2">
                <input 
                  type="number" 
                  placeholder="KES Amount" 
                  className="w-full text-sm p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" 
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <button 
                  onClick={handleWelfareSubmit}
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 rounded-xl font-bold text-sm shadow-lg shadow-blue-200 transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSubmitting ? '...' : 'Add'}
                </button>
              </div>

              <button 
                onClick={() => onViewChange('welfare-details')}
                className="w-full text-xs text-gray-400 font-bold hover:text-blue-600 transition-colors pt-2 flex items-center justify-center gap-1"
              >
                VIEW MONTHLY REPORT →
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
            <h3 className="font-black text-gray-900 mb-4 uppercase text-xs tracking-widest">
              WhatsApp Broadcast
            </h3>
            <div className="space-y-3">
              {/* Message Input Area */}
              <textarea 
                placeholder="Type newsletter message here..."
                className="w-full text-sm p-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-purple-500 outline-none min-h-[100px] resize-none"
                value={newsletterContent}
                onChange={(e) => setNewsletterContent(e.target.value)}
              />
    
              <button 
                onClick={handleNewsletterSubmit}
                disabled={isSendingNewsletter}
                className="w-full bg-gray-900 text-white py-3 rounded-xl text-sm font-bold hover:bg-black transition shadow-md active:scale-95 disabled:opacity-50"
              >
                {isSendingNewsletter ? 'Sending...' : '🚀 Send Newsletter'}
              </button>
            </div>
          </div>
           

          <BookingApprovalsModal 
            isOpen={isApprovalsOpen} 
            onClose={() => setIsApprovalsOpen(false)} 
            shopId={user?.shop_id || 68} 
          />        

          <SacramentApprovalsModal
            isOpen={isSacramentsOpen}
            onClose={() => setIsSacramentsOpen(false)}
            shopId={user?.shop_id || 68}
            user={user}
          />

        </div>
      </div>
    </div>
  );
};
